import { Component } from '@theme/component';

/**
 * A custom element that formats rte content for easier styling
 */
class RTEFormatter extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.querySelectorAll('table').forEach(this.#formatTable);
    this.#setupCollapsibleDescription();
  }

  /**
   * Formats a table for easier styling
   * @param {HTMLTableElement} table
   */
  #formatTable(table) {
    const wrapper = document.createElement('div');
    wrapper.classList.add('rte-table-wrapper');
    const parent = table.parentNode;
    if (parent) {
      parent.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }
  }

  /**
   * Collapses long product descriptions and adds a Learn More toggle button
   */
  #setupCollapsibleDescription() {
    // Only apply if inside product details/information containers or if marked as collapsible
    const isProductPage = !!this.closest('.product-details, .product-information, [data-testid="product-information-details"]');
    if (!isProductPage) return;

    // Prevent duplicate wrapping
    if (this.querySelector('.rte-description-inner')) return;

    // Define collapse threshold height
    const thresholdHeight = 190;
    if (this.scrollHeight <= thresholdHeight) return;

    // Wrap child elements in container
    const inner = document.createElement('div');
    inner.classList.add('rte-description-inner', 'is-collapsed');

    while (this.firstChild) {
      inner.appendChild(this.firstChild);
    }
    this.appendChild(inner);

    // Create Learn More toggle button
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'product-description__toggle-btn';
    btn.setAttribute('aria-expanded', 'false');
    btn.innerHTML = `
      <span class="btn-text">Learn More</span>
      <span class="svg-wrapper icon-caret">
        <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </span>
    `;

    btn.addEventListener('click', () => {
      const isExpanded = btn.getAttribute('aria-expanded') === 'true';
      const textSpan = btn.querySelector('.btn-text');

      if (isExpanded) {
        inner.classList.remove('is-expanded');
        inner.classList.add('is-collapsed');
        btn.setAttribute('aria-expanded', 'false');
        if (textSpan) textSpan.textContent = 'Learn More';

        const rect = this.getBoundingClientRect();
        if (rect.top < 0) {
          this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } else {
        inner.classList.remove('is-collapsed');
        inner.classList.add('is-expanded');
        btn.setAttribute('aria-expanded', 'true');
        if (textSpan) textSpan.textContent = 'Show Less';
      }
    });

    this.appendChild(btn);
  }
}

if (!customElements.get('rte-formatter')) {
  customElements.define('rte-formatter', RTEFormatter);
}
